// apps-drag.js

import state from './apps-state.js';
import * as render from './apps-render.js';
import * as api from './apps-api.js';
import * as nav from './apps-navigation.js';
import * as iconops from './apps-icons.js';
import * as folder from './apps-folders.js';
import * as navLogic from './apps-kb-navigation-logic.js';

let scrollTimerCount = 0;
let closeTimerCount = 0;

// ===== DOM QUERIES =====
function getIndicator() {
    return state.openFolder ? 
        document.getElementById('folderInsertIndicator') : 
        document.getElementById('insertIndicator');
}

function getPreview() {
    return state.openFolder ? 
        document.getElementById('folderDropPreview') : 
        document.getElementById('dropPreview');
}

function getViewportInfo() {
    const viewportEl = state.openFolder ? 
        document.getElementById('folderContainer') : 
        document.getElementById('appspaceViewport');
    return {
        element: viewportEl,
        rect: viewportEl.getBoundingClientRect(),
        leftOffset: 0
    };
}

function getCurrentScreenSlots() {
    const containerSelector = state.openFolder ? '.folder-content' : '.appspace-viewport';
    let allSlots = document.querySelectorAll(`${containerSelector} .icon-slot`);
    const layout = render.calculateLayout();
    
    if (state.openFolder) {
        const regionsPerScreen = layout.leaves;
        const screenStartRegion = state.currentFolderScreen * regionsPerScreen;
        const screenEndRegion = screenStartRegion + regionsPerScreen - 1;
        
        return Array.from(allSlots).filter(slot => {
            const region = parseInt(slot.dataset.region);
            return region >= screenStartRegion && region <= screenEndRegion;
        });
    } else {
        const regionsPerScreen = layout.leaves * layout.bands;
        const screenStartRegion = state.currentScreen * regionsPerScreen;
        const screenEndRegion = screenStartRegion + regionsPerScreen - 1;
        
        return Array.from(allSlots).filter(slot => {
            const region = parseInt(slot.dataset.region);
            return region >= screenStartRegion && region <= screenEndRegion;
        });
    }
}

// ===== COMPUTED STYLES =====
function getComputedDimensions() {
    const iconSize = parseInt(
        getComputedStyle(document.documentElement)
            .getPropertyValue('--icon-size')
    ) || state.config.iconSize;
    
    const gapH = parseInt(
        getComputedStyle(document.documentElement)
            .getPropertyValue('--icon-gap-horizontal')
    ) || state.config.gapHorizontal;
    
    const gapV = parseInt(
        getComputedStyle(document.documentElement)
            .getPropertyValue('--icon-gap-vertical')
    ) || state.config.gapVertical;
    
    return { iconSize, gapH, gapV };
}

// ===== VALIDATION =====
function isOriginalDragPosition(region, position) {
    const currentContext = state.openFolder ? state.openFolder.id : 0;
    return state.dragState && 
        state.dragState.originalFolder === currentContext &&
        region === state.dragState.originalRegion && 
        position === state.dragState.originalPosition;
}

function canMergeIcons(targetIconData) {
    if (!state.dragState) return false;
    
    // Can't merge with self
    if (targetIconData.id === state.dragState.icon.id) return false;
    
    // Can't merge folders
    if (state.dragState.icon.type === 'folder') return false;
    
    // Can't drop folder on folder
    if (state.dragState.icon.type === 'folder' && targetIconData.type === 'folder') {
        return false;
    }
    
    return true;
}

// ===== VISUAL FEEDBACK =====

// Store references to elements we clear frequently
let cachedDragOverElements = [];

export function clearDropIndicators() {
    // Only clear if we have cached elements
    cachedDragOverElements.forEach(el => {
        if (el && el.classList) {
            el.classList.remove('drag-over');
        }
    });
    cachedDragOverElements = [];
    
    // Clear indicators
    const indicators = [
        document.getElementById('insertIndicator'),
        document.getElementById('folderInsertIndicator')
    ];
    indicators.forEach(ind => ind && ind.classList.remove('visible'));
    
    // Clear previews
    const previews = [
        document.getElementById('dropPreview'),
        document.getElementById('folderDropPreview')
    ];
    previews.forEach(preview => preview && preview.classList.remove('visible'));
    
    // Clear drop intent
    state.intendedDropAction = null;
}

// When you ADD drag-over class somewhere, also cache it
export function addDragOverClass(element) {
    element.classList.add('drag-over');
    if (!cachedDragOverElements.includes(element)) {
        cachedDragOverElements.push(element);
    }
}

function showDropPreview(slotRect, region, position) {
    const preview = getPreview();
    if (!preview) return;
    
    const { rect: viewportRect, leftOffset } = getViewportInfo();
    const { iconSize } = getComputedDimensions();
    
    preview.style.left = (slotRect.left - viewportRect.left + leftOffset) + 'px';
    preview.style.top = (slotRect.top - viewportRect.top) + 'px';
    preview.style.width = slotRect.width + 'px';
    preview.style.height = iconSize + 'px';
    // Only add class if not already present
    if (!preview.classList.contains('visible')) {
        preview.classList.add('visible');
    }
    
    state.intendedDropAction = {
        type: 'move',
        region,
        position,
        folder: state.openFolder ? state.openFolder.id : 0
    };
}

function showMergeIndicator(iconElement, targetIconId) {
    addDragOverClass(iconElement);
    state.intendedDropAction = {
        type: 'merge',
        targetIconId,
        folder: state.openFolder ? state.openFolder.id : 0
    };
}

function showInsertIndicator(gapRect, region, position, insertBefore) {
    const indicator = getIndicator();
    if (!indicator) return;
    
    const { rect: viewportRect, leftOffset } = getViewportInfo();
    
    indicator.style.left = (gapRect.left - viewportRect.left + leftOffset) + 'px';
    indicator.style.top = (gapRect.top - viewportRect.top) + 'px';
    indicator.style.width = gapRect.width + 'px';
    indicator.style.height = gapRect.height + 'px';
    indicator.style.borderRadius = '4px';
    indicator.classList.add('visible');
    
    state.intendedDropAction = {
        type: insertBefore ? 'insert' : 'append',
        region,
        position,
        insertBefore,
        folder: state.openFolder ? state.openFolder.id : 0
    };
}

// ===== ROW SPACE CHECKING =====
export function hasSpaceInRank(rank, layout, excludeDraggedIcon = true, isFolder = false) {
    const currentScreen = isFolder ? state.currentFolderScreen : state.currentScreen;
    const icons = isFolder ? 
        window.icons.filter(icon => icon.kfolder === state.openFolder.id) :
        window.icons.filter(icon => icon.kfolder === 0);
    
    const totalFiles = layout.leaves * 4;
    
    // Count occupied slots
    let occupiedCount = 0;
    
    for (let file = 0; file < totalFiles; file++) {
        if (navLogic.isSlotOccupied(rank, file, icons, layout, isFolder, currentScreen)) {
            occupiedCount++;
        }
    }
    
    // If excluding dragged icon and it's in this rank, we have space
    if (excludeDraggedIcon && state.dragState) {
        const draggedVisualPos = render.calculateVisualPosition(
            state.dragState.originalRegion, 
            state.dragState.originalPosition, 
            layout
        );
        if (draggedVisualPos.rank === rank) {
            occupiedCount--; // Dragged icon doesn't count
        }
    }
    
    return occupiedCount < totalFiles;
}

// ===== MAIN ENTRY POINT =====
export function updateDropTarget(clientX, clientY) {
    clearDropIndicators();
    
    const layout = render.calculateLayout();
    const isFolder = !!state.openFolder;
    
    // Convert pixel coordinates to rank/file
    const rankFile = navLogic.pixelToRankFile(clientX, clientY, layout, isFolder);
    
    if (!rankFile) {
        // Outside grid entirely
        return;
    }
    
    const { rank, file } = rankFile;
    
    // Handle special zones
    if (rank === -1 || rank > layout.bands * 3) {
        // Modal zones - not valid drop targets
        return;
    }
    
    // Handle scroll zones
    if (file === -1 || file >= layout.leaves * 4) {
        // Scroll zones - not valid drop targets for dropping
        return;
    }
    
    // Now we have a valid rank/file position
    // Determine what type of drop target this represents
    handleDropTargetAtRankFile(rank, file, clientX, clientY, layout, isFolder);
}

// Helper: Convert rank/file to region/position (folder-aware)
function rankFileToRegionPos(rank, file, layout, isFolder) {
    if (isFolder) {
        const regionLeaf = Math.floor(file / 4);
        const region = regionLeaf;
        const posRank = rank % 3;
        const posFile = file % 4;
        const position = posRank * 4 + posFile;
        return { region, position };
    }
    return render.visualPositionToRegionPosition(rank, file, layout);
}

function handleDropTargetAtRankFile(rank, file, clientX, clientY, layout, isFolder) {
    const currentScreen = isFolder ? state.currentFolderScreen : state.currentScreen;
    const icons = isFolder ? 
        window.icons.filter(icon => icon.kfolder === state.openFolder.id) :
        window.icons.filter(icon => icon.kfolder === 0);
    
    // Check if this slot is occupied
    const isOccupied = navLogic.isSlotOccupied(rank, file, icons, layout, isFolder, currentScreen);
    
    if (Number.isInteger(file)) {
        // Integer file - either empty slot or merge target
        if (isOccupied) {
            // Get the icon at this position
            const slotInfo = rankFileToRegionPos(rank, file, layout, isFolder);
            if (!slotInfo) return;
            
            const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
            const screenStartRegion = currentScreen * regionsPerScreen;
            const actualRegion = screenStartRegion + slotInfo.region;
            
            const icon = icons.find(i => i.region === actualRegion && i.position === slotInfo.position);
            
            if (icon && icon.id !== state.dragState?.icon.id) {
                // Valid merge target
                const elements = document.elementsFromPoint(clientX, clientY);
                const iconElement = elements.find(el => 
                    el.classList.contains('appicon') && 
                    el.dataset.iconId === icon.id.toString()
                );
                if (iconElement) {
                    showMergeIndicator(iconElement, icon.id);
                }
            }
        } else {
            // Empty slot - show drop preview
            const slotInfo = rankFileToRegionPos(rank, file, layout, isFolder);
            if (!slotInfo) return;
            
            const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
            const screenStartRegion = currentScreen * regionsPerScreen;
            const actualRegion = screenStartRegion + slotInfo.region;
            
            // Scope query to correct container
            const container = isFolder ? 
                document.getElementById('folderScreens') : 
                document.getElementById('appspaceScreens');
            
            if (!container) return;
            
            // Find the slot element
            const slotElement = container.querySelector(
                `.icon-slot[data-region="${actualRegion}"][data-position="${slotInfo.position}"]`
            );
            
            if (slotElement) {
                const slotRect = slotElement.getBoundingClientRect();
                showDropPreview(slotRect, actualRegion, slotInfo.position);
            }
        }
    } else {
        // Fractional file - insert point
        // Validate this is a legitimate insert point
        const draggedIcon = state.dragState?.icon;
        if (!draggedIcon) return;
        
        const isValid = navLogic.isValidInsertPosition(rank, file, draggedIcon, icons, layout, isFolder, currentScreen);
        
        if (isValid) {
            // Show insert indicator
            showInsertIndicatorAtRankFile(rank, file, layout, isFolder, currentScreen);
        } else {
            // Gap is ineligible - fall back to choosing nearest blank or occupied slot
            // Calculate left and right files from the fractional position
            const leftFile = Math.floor(file);
            const rightFile = Math.ceil(file);
            
            // Calculate which side is closer to the mouse cursor based on fractional part
            const fractionalPart = file - leftFile;
            const distToLeft = fractionalPart;
            const distToRight = 1 - fractionalPart;
            
            // Determine priority order based on distance
            const tryLeftFirst = distToLeft < distToRight;
            const firstFile = tryLeftFirst ? leftFile : rightFile;
            const secondFile = tryLeftFirst ? rightFile : leftFile;
            
            // Pre-calculate these for use in nested scopes
            const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
            const screenStartRegion = currentScreen * regionsPerScreen;
            
            // Try first file
            const firstOccupied = navLogic.isSlotOccupied(rank, firstFile, icons, layout, isFolder, currentScreen);
            
            if (!firstOccupied) {
                // First slot is blank - show drop preview
                const slotInfo = rankFileToRegionPos(rank, firstFile, layout, isFolder);
                if (slotInfo) {
                    const actualRegion = screenStartRegion + slotInfo.region;
                    
                    const container = isFolder ? 
                        document.getElementById('folderScreens') : 
                        document.getElementById('appspaceScreens');
                    
                    if (container) {
                        const slotElement = container.querySelector(
                            `.icon-slot[data-region="${actualRegion}"][data-position="${slotInfo.position}"]`
                        );
                        
                        if (slotElement) {
                            const slotRect = slotElement.getBoundingClientRect();
                            showDropPreview(slotRect, actualRegion, slotInfo.position);
                        }
                    }
                }
            } else {
                // First slot is occupied - check if we can merge
                const slotInfo = rankFileToRegionPos(rank, firstFile, layout, isFolder);
                if (slotInfo) {
                    const actualRegion = screenStartRegion + slotInfo.region;
                    
                    const icon = icons.find(i => i.region === actualRegion && i.position === slotInfo.position);
                    
                    if (icon && icon.id !== state.dragState?.icon.id) {
                        const elements = document.elementsFromPoint(clientX, clientY);
                        const iconElement = elements.find(el => 
                            el.classList.contains('appicon') && 
                            el.dataset.iconId === icon.id.toString()
                        );
                        if (iconElement) {
                            showMergeIndicator(iconElement, icon.id);
                        }
                    } else {
                        // Can't merge with first slot, try second file
                        const secondOccupied = navLogic.isSlotOccupied(rank, secondFile, icons, layout, isFolder, currentScreen);
                        
                        if (!secondOccupied) {
                            // Second slot is blank - show drop preview
                            const slotInfo2 = rankFileToRegionPos(rank, secondFile, layout, isFolder);
                            if (slotInfo2) {
                                const actualRegion2 = screenStartRegion + slotInfo2.region;
                                
                                const container = isFolder ? 
                                    document.getElementById('folderScreens') : 
                                    document.getElementById('appspaceScreens');
                                
                                if (container) {
                                    const slotElement = container.querySelector(
                                        `.icon-slot[data-region="${actualRegion2}"][data-position="${slotInfo2.position}"]`
                                    );
                                    
                                    if (slotElement) {
                                        const slotRect = slotElement.getBoundingClientRect();
                                        showDropPreview(slotRect, actualRegion2, slotInfo2.position);
                                    }
                                }
                            }
                        } else {
                            // Second slot is also occupied - try merge
                            const slotInfo2 = rankFileToRegionPos(rank, secondFile, layout, isFolder);
                            if (slotInfo2) {
                                const actualRegion2 = screenStartRegion + slotInfo2.region;
                                const icon2 = icons.find(i => i.region === actualRegion2 && i.position === slotInfo2.position);
                                
                                if (icon2 && icon2.id !== state.dragState?.icon.id) {
                                    const elements = document.elementsFromPoint(clientX, clientY);
                                    const iconElement = elements.find(el => 
                                        el.classList.contains('appicon') && 
                                        el.dataset.iconId === icon2.id.toString()
                                    );
                                    if (iconElement) {
                                        showMergeIndicator(iconElement, icon2.id);
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

function showInsertIndicatorAtRankFile(rank, file, layout, isFolder, currentScreen) {
    const maxFile = layout.leaves * 4 - 1;
    
    // Special handling for -0.5 (insert before file 0)
    if (file === -0.5) {
        const slotInfo = rankFileToRegionPos(rank, 0, layout, isFolder);
        if (!slotInfo) return;
        
        const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
        const screenStartRegion = currentScreen * regionsPerScreen;
        const actualRegion = screenStartRegion + slotInfo.region;
        
        const container = isFolder ? 
            document.getElementById('folderScreens') : 
            document.getElementById('appspaceScreens');
        
        if (!container) return;
        
        const slot = container.querySelector(`.icon-slot[data-region="${actualRegion}"][data-position="${slotInfo.position}"]`);
        if (!slot) return;
        
        const slotRect = slot.getBoundingClientRect();
        
        // Get the actual gap width from computed styles
        const { gapH } = getComputedDimensions();
        
        const gapRect = {
            left: slotRect.left - gapH,
            top: slotRect.top,
            width: gapH,
            height: slotRect.height
        };
        
        showInsertIndicator(gapRect, actualRegion, slotInfo.position, true);
        return;
    }
    
    // Special handling for MAX+0.5 (append after file MAX)
    if (file === maxFile + 0.5) {
        const slotInfo = rankFileToRegionPos(rank, maxFile, layout, isFolder);
        if (!slotInfo) return;
        
        const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
        const screenStartRegion = currentScreen * regionsPerScreen;
        const actualRegion = screenStartRegion + slotInfo.region;
        
        const container = isFolder ? 
            document.getElementById('folderScreens') : 
            document.getElementById('appspaceScreens');
        
        if (!container) return;
        
        const slot = container.querySelector(`.icon-slot[data-region="${actualRegion}"][data-position="${slotInfo.position}"]`);
        if (!slot) return;
        
        const slotRect = slot.getBoundingClientRect();
        
        // Get the actual gap width from computed styles
        const { gapH } = getComputedDimensions();
        
        const gapRect = {
            left: slotRect.right,
            top: slotRect.top,
            width: gapH,
            height: slotRect.height
        };
        
        showInsertIndicator(gapRect, actualRegion, slotInfo.position, false);
        return;
    }
    
    // Regular handling for X.5 positions (between two adjacent icons)
    // Determine which icons are on either side of this insert point
    const leftFile = Math.floor(file);
    const rightFile = Math.ceil(file);
    
    // Get slot elements for positioning
    const leftSlotInfo = rankFileToRegionPos(rank, leftFile, layout, isFolder);
    const rightSlotInfo = rankFileToRegionPos(rank, rightFile, layout, isFolder);
    
    if (!leftSlotInfo || !rightSlotInfo) return;
    
    const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
    const screenStartRegion = currentScreen * regionsPerScreen;
    
    const leftRegion = screenStartRegion + leftSlotInfo.region;
    const rightRegion = screenStartRegion + rightSlotInfo.region;
    
    // Scope query to correct container
    const container = isFolder ? 
        document.getElementById('folderScreens') : 
        document.getElementById('appspaceScreens');
    
    if (!container) return;
    
    const leftSlot = container.querySelector(`.icon-slot[data-region="${leftRegion}"][data-position="${leftSlotInfo.position}"]`);
    const rightSlot = container.querySelector(`.icon-slot[data-region="${rightRegion}"][data-position="${rightSlotInfo.position}"]`);
    
    if (!leftSlot || !rightSlot) return;
    
    const leftRect = leftSlot.getBoundingClientRect();
    const rightRect = rightSlot.getBoundingClientRect();
    
    // Show indicator in the gap
    const gapRect = {
        left: leftRect.right,
        top: Math.min(leftRect.top, rightRect.top),
        width: rightRect.left - leftRect.right,
        height: Math.max(leftRect.bottom, rightRect.bottom) - Math.min(leftRect.top, rightRect.top)
    };
    
    showInsertIndicator(gapRect, rightRegion, rightSlotInfo.position, true);
}


export function updateDragging(clientX, clientY) {
    if (!state.dragState || !state.dragGhost) return;

    state.dragState.currentX = clientX;
    state.dragState.currentY = clientY;

    const computedIconSize = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--icon-size')) || state.config.iconSize;
    // Update ghost position
    state.dragGhost.style.left = (clientX - computedIconSize / 2) + 'px';
    state.dragGhost.style.top = (clientY - computedIconSize / 2) + 'px';
    // Check if dragging outside folder bounds (only vertically, not horizontally)
    if (state.openFolder) {
        const folderContainer = document.getElementById('folderContainer');
        const folderRect = folderContainer.getBoundingClientRect();

        // Only check vertical bounds (above or below), not horizontal (left or right)
        const isOutsideFolder = clientY < folderRect.top || clientY > folderRect.bottom;

        if (isOutsideFolder) {
            if (!state.folderCloseTimer) {
                closeTimerCount++;
                console.log('Starting folder close timer');
                state.folderCloseTimer = setTimeout(() => {
                    console.log('Closing folder due to drag outside');
                    folder.closeFolderView();
                    state.folderCloseTimer = null;
                }, 1000);
            }
        } else {
            if (state.folderCloseTimer) {
                clearTimeout(state.folderCloseTimer);
                state.folderCloseTimer = null;
            }
        }
    }

    // Check for scroll zones
    if (state.openFolder) {
        // ========== FOLDER SCROLL ZONES ==========
        const folderContainer = document.getElementById('folderContainer');
        const folderRect = folderContainer.getBoundingClientRect();
        const scrollZoneWidth = 50;
        const screensContainer = document.getElementById('folderScreens');
        const actualScreens = screensContainer.querySelectorAll('.folder-screen:not(.temporary)').length;

        // Check left scroll zone
        if (clientX >= folderRect.left &&
            clientX < folderRect.left + scrollZoneWidth) {

            if (!state.scrollHoverTimer) {
                scrollTimerCount++;
                console.log('Starting folder left scroll timer');
                state.scrollHoverTimer = setTimeout(async () => {
                    console.log('Scrolling folder left, current screen:', state.currentFolderScreen);
                    if (state.currentFolderScreen === 0 && state.temporaryScreenSide !== 'left') {
                        await nav.createTemporaryFolderScreenToLeft();
                    } else if (state.currentFolderScreen > 0) {
                        nav.goToFolderScreen(state.currentFolderScreen - 1);
                    }
                    state.scrollHoverTimer = null;
                }, state.config.scrollHoverDuration);
            }
        }
        // Check right scroll zone
        else if (clientX > folderRect.right - scrollZoneWidth &&
                 clientX <= folderRect.right) {

            if (!state.scrollHoverTimer) {
                scrollTimerCount++;
                console.log('Starting folder right scroll timer');
                state.scrollHoverTimer = setTimeout(() => {
                    console.log('Scrolling folder right, current screen:', state.currentFolderScreen);
                    const maxScreens = screensContainer.children.length;

                    if (state.currentFolderScreen === actualScreens - 1 && !state.temporaryScreenSide) {
                        nav.createTemporaryFolderScreenToRight();
                    } else if (state.currentFolderScreen < maxScreens - 1) {
                        nav.goToFolderScreen(state.currentFolderScreen + 1);
                    }
                    state.scrollHoverTimer = null;
                }, state.config.scrollHoverDuration);
            }
        }
        else {
            if (state.scrollHoverTimer) {
                clearTimeout(state.scrollHoverTimer);
                state.scrollHoverTimer = null;
            }
        }
    } else {
        // ========== MAIN SCREEN SCROLL ZONES ==========
        const viewport = document.getElementById('appspaceViewport');
        const viewportRect = viewport.getBoundingClientRect();
        const scrollZoneWidth = 50;
        const screensContainer = document.getElementById('appspaceScreens');
        const actualScreens = screensContainer.querySelectorAll('.appspace-screen:not(.temporary)').length;

        // Check left scroll zone
        if (clientX >= viewportRect.left &&
            clientX < viewportRect.left + scrollZoneWidth) {

            if (!state.scrollHoverTimer) {
                scrollTimerCount++;
                console.log('Starting main screen left scroll timer');
                state.scrollHoverTimer = setTimeout(async () => {
                    console.log('Scrolling main screen left');
                    if (state.currentScreen === 0 && state.temporaryScreenSide !== 'left') {
                        await nav.createTemporaryScreenToLeft();
                    } else if (state.currentScreen > 0) {
                        nav.goToScreen(state.currentScreen - 1);
                    }
                    state.scrollHoverTimer = null;
                }, state.config.scrollHoverDuration);
            }
        }
        else if (clientX > viewportRect.right - scrollZoneWidth &&
                 clientX <= viewportRect.right) {

            if (!state.scrollHoverTimer) {
                scrollTimerCount++;
                console.log('Starting main screen right scroll timer');
                state.scrollHoverTimer = setTimeout(() => {
                    console.log('Scrolling main screen right');
                    const maxScreens = screensContainer.children.length;

                    if (state.currentScreen === actualScreens - 1 && !state.temporaryScreenSide) {
                        nav.createTemporaryScreenToRight();
                    } else if (state.currentScreen < maxScreens - 1) {
                        nav.goToScreen(state.currentScreen + 1);
                    }
                    state.scrollHoverTimer = null;
                }, state.config.scrollHoverDuration);
            }
        }
        else {
            if (state.scrollHoverTimer) {
                clearTimeout(state.scrollHoverTimer);
                state.scrollHoverTimer = null;
            }
        }
    }

    // Find drop target
    updateDropTarget(clientX, clientY);
}

// Helper function to re-select an icon after insert/append in keyboard move mode
async function reselectIconAfterInsert(iconId) {
    // Import the cursor module
    const cursorModule = await import('./apps-kb-cursor.js');
    if (!cursorModule || typeof cursorModule.showVirtualCursor !== 'function') {
        console.warn('Could not import showVirtualCursor');
        return;
    }
    
    // Find the icon by ID
    const icon = window.icons.find(ic => ic.id === iconId);
    if (!icon) {
        console.warn('Could not find icon to reselect:', iconId);
        return;
    }
    
    // Calculate visual position
    const layout = render.calculateLayout();
    const isFolder = icon.kfolder !== 0;
    
    let rank, file;
    if (isFolder && state.openFolder && state.openFolder.id === icon.kfolder) {
        // Folder view: simpler calculation
        const posRank = Math.floor(icon.position / 4);
        const posFile = icon.position % 4;
        const regionLeaf = icon.region;
        rank = posRank;
        file = regionLeaf * 4 + posFile;
    } else if (!isFolder) {
        // Main screen: use calculateVisualPosition
        const visualPos = render.calculateVisualPosition(icon.region, icon.position, layout);
        rank = visualPos.rank;
        file = visualPos.file;
    } else {
        // Icon is in a folder that's not currently open - don't reselect
        return;
    }
    
    // Re-select the icon at its new position
    cursorModule.showVirtualCursor(rank, file);
    console.log('Re-selected icon after insert/append:', { iconId, rank, file });
}

export async function endDragging(_clientX, _clientY) {
    if (!state.dragState) {
        console.error('endDragging called but no state.dragState');
        
        // Even without dragState, clean up keyboard navigation elements
        import('./apps-kb-cursor.js').then(cursor => {
            if (cursor && typeof cursor.hideVirtualCursor === 'function') {
                cursor.hideVirtualCursor();
            }
        });
        
        import('./apps-kb-scroll.js').then(scroll => {
            if (scroll && typeof scroll.deactivateScrollZones === 'function') {
                scroll.deactivateScrollZones();
            }
        });
        
        import('./apps-kb-modal.js').then(modal => {
            if (modal && typeof modal.unhighlightModalZone === 'function') {
                modal.unhighlightModalZone('top');
                modal.unhighlightModalZone('bottom');
            }
        });
        
        clearDropIndicators();
        
        import('./apps-kb-move-mode.js').then(moveMode => {
            if (moveMode && typeof moveMode.exitMoveMode === 'function') {
                moveMode.exitMoveMode(false);
            }
        });
        
        cancelDragging();
        return;
    }
    
    // CRITICAL FIX: Immediately set a flag to prevent duplicate processing
    if (state.isProcessingDrop) {
        console.warn('Drop already being processed, ignoring duplicate call');
        return;
    }
    state.isProcessingDrop = true;
    
    // Save state.dragState values BEFORE any operations that might clear it
    const savedOriginalFolder = state.dragState.originalFolder;
    const savedOriginalRegion = state.dragState.originalRegion;
    const savedOriginalPosition = state.dragState.originalPosition;
    const savedIconId = state.dragState.icon.id;
    const savedIconFolder = state.dragState.icon.kfolder;
    const isKeyboardDrag = state.dragState.isKeyboardDrag || false;
    const handedOffToMouse = state.dragState.handedOffToMouse || false;
    
    // Use the intended drop action that was determined during hover
    let success = false;
    let isInvalidDrop = false;
    let droppedOnTemporary = false;
    let tempSide = state.temporaryScreenSide; // Save before cancelDragging clears it
    
    // Save drop action type to know if we need to re-select after insert/append
    const dropActionType = state.intendedDropAction ? state.intendedDropAction.type : null;
    const needsReselect = isKeyboardDrag && !handedOffToMouse && 
        (dropActionType === 'insert' || dropActionType === 'append');
    
    if (state.intendedDropAction) {
        // Check if dropping on a temporary screen
        // Need to verify we're on the temporary screen visually, not just check region numbers
        if (tempSide && state.intendedDropAction.region !== undefined) {
            const isFolder = state.intendedDropAction.folder !== 0;
            const currentScreen = isFolder ? state.currentFolderScreen : state.currentScreen;
            
            if (tempSide === 'left') {
                // Left temporary screen is always at screen index 0
                if (currentScreen === 0) {
                    droppedOnTemporary = true;
                }
            } else if (tempSide === 'right') {
                // Right temporary screen - check if on the last screen
                const screensContainer = isFolder ? 
                    document.getElementById('folderScreens') : 
                    document.getElementById('appspaceScreens');
                const totalScreens = screensContainer ? screensContainer.children.length : 0;
                const lastScreenIndex = totalScreens - 1;
                
                if (currentScreen === lastScreenIndex) {
                    droppedOnTemporary = true;
                }
            }
        }
        
        if (state.intendedDropAction.type === 'move') {
            state.dragState.icon.kfolder = state.intendedDropAction.folder;
            state.dragState.icon.region = state.intendedDropAction.region;
            state.dragState.icon.position = state.intendedDropAction.position;
            api.updateIcon(state.dragState.icon);
            success = true;
        } else if (state.intendedDropAction.type === 'insert') {
            const targetFolder = state.intendedDropAction.folder;
            iconops.insertIconAtPosition(state.dragState.icon, state.intendedDropAction.region, state.intendedDropAction.position, state.intendedDropAction.insertBefore, targetFolder);
            success = true;
        } else if (state.intendedDropAction.type === 'append') {
            const targetFolder = state.intendedDropAction.folder;
            iconops.insertIconAtPosition(state.dragState.icon, state.intendedDropAction.region, state.intendedDropAction.position, false, targetFolder);
            success = true;
        } else if (state.intendedDropAction.type === 'merge') {
            // Check for invalid folder operations
            if (state.dragState.icon.type === 'folder' || state.openFolder) {
                console.log('Invalid: Cannot merge folders or merge inside folder');
                isInvalidDrop = true;
            } else {
                const targetIconData = window.icons.find(ic => ic.id === state.intendedDropAction.targetIconId);
                if (targetIconData) {
                    let targetFolderId;
                    if (targetIconData.type === 'folder') {
                        // Merging into existing folder
                        targetFolderId = targetIconData.id;
                        state.dragState.icon.kfolder = targetFolderId;
                        const nextPos = iconops.findFirstAvailablePosition(targetFolderId);
                        state.dragState.icon.region = nextPos.region;
                        state.dragState.icon.position = nextPos.position;
                        api.updateIcon(state.dragState.icon);
                    } else {
                        // Creating new folder - createFolder handles both icons
                        targetFolderId = await iconops.createFolder(targetIconData, state.dragState.icon);
                        // Don't call iconops.findFirstAvailablePosition - iconops.createFolder already placed both icons
                    }
                    success = true;
                } else {
                    isInvalidDrop = true;
                }
            }
        }
    } else {
        // No valid drop target shown
        isInvalidDrop = true;
    }

    // Clear the intent
    state.intendedDropAction = null;
    
    // Clear interaction flag
    state.isInteractingWithIcon = false;
    state.iconPressStart = null;
    
    // CRITICAL: Clear the processing flag before any async operations or renders
    state.isProcessingDrop = false;
    
    if (isInvalidDrop || !success) {
        console.log('Invalid drop or drop failed, animating back to original position');
        animateIconBack();
    } else {
        // Success! Now handle cleanup
        const wasOnTemporary = droppedOnTemporary;
        const whichSide = tempSide;
        
        // Cancel dragging FIRST (clears state.dragState and visual state)
        const savedTempSide = state.temporaryScreenSide;
        state.temporaryScreenSide = null; // Prevent cancelDragging from removing screens
        cancelDragging();
        state.temporaryScreenSide = savedTempSide; // Restore for conversion
        
        // Hide keyboard UI if this was a pure mouse drag OR if keyboard drag was handed off to mouse
        const shouldHideKeyboardUI = !isKeyboardDrag || handedOffToMouse;
        
        if (shouldHideKeyboardUI) {
            // This is a mouse/touch drag - hide all keyboard UI and exit move mode
            import('./apps-kb-cursor.js').then(cursor => {
                if (cursor && typeof cursor.hideVirtualCursor === 'function') {
                    cursor.hideVirtualCursor();
                }
            });
            
            import('./apps-kb-scroll.js').then(scroll => {
                if (scroll && typeof scroll.deactivateScrollZones === 'function') {
                    scroll.deactivateScrollZones();
                }
            });
            
            import('./apps-kb-modal.js').then(modal => {
                if (modal && typeof modal.unhighlightModalZone === 'function') {
                    modal.unhighlightModalZone('top');
                    modal.unhighlightModalZone('bottom');
                }
            });
            
            import('./apps-kb-move-mode.js').then(moveMode => {
                if (moveMode && typeof moveMode.exitMoveMode === 'function') {
                    moveMode.exitMoveMode(false); // Don't cancel, operation succeeded
                }
            });
        }
        // For pure keyboard drags (not handed off), exitMoveMode is called from the keyboard module
        // which will handle cleanup appropriately
        
        // Always clear drop indicators regardless of drag type
        clearDropIndicators();
        
        // Check if we left a folder with only 1 item
        if (savedOriginalFolder !== 0) {
            const folderIcons = window.icons.filter(ic => ic.kfolder === savedOriginalFolder);
            if (folderIcons.length === 1) {
                // Move the last icon to where the folder was
                const lastIcon = folderIcons[0];
                const folderIcon = window.icons.find(ic => ic.id === savedOriginalFolder);
                if (folderIcon) {
                    lastIcon.kfolder = 0;
                    lastIcon.region = folderIcon.region;
                    lastIcon.position = folderIcon.position;
                    
                    // Delete the folder
                    const folderIndex = window.icons.findIndex(ic => ic.id === savedOriginalFolder);
                    if (folderIndex >= 0) {
                        window.icons.splice(folderIndex, 1);
                    }
                    
                    // CRITICAL: Update icon FIRST, then delete folder
                    // Must await to ensure icon is moved out before folder is deleted
                    await api.updateIcon(lastIcon);
                    await api.deleteFolder(savedOriginalFolder);
                    
                    // If folder was open, close it
                    if (state.openFolder && state.openFolder.id === savedOriginalFolder) {
                        folder.closeFolderView();
                        render.renderLaunchpad();
                        return;
                    }
                }
            }
        }
        
        // If dropped on temporary screen, convert it to real screen
        if (wasOnTemporary && whichSide) {
            (async () => {
                const layout = render.calculateLayout();
                
                // Check if this is a folder or main screen
                if (savedIconFolder !== 0) {
                    // ===== FOLDER TEMPORARY SCREEN =====
                    const regionsPerScreen = layout.leaves;
                    const folderId = savedIconFolder;
                    const screensContainer = document.getElementById('folderScreens');
                    
                    if (whichSide === 'left') {
                        const tempScreen = screensContainer.querySelector('.folder-screen.temporary');
                        if (tempScreen) {
                            tempScreen.classList.remove('temporary');
                        }
                        
                        // Insert regions on backend
                        const result = await api.insertRegions(0, regionsPerScreen, folderId);
                        
                        // Collect icons to shift (exclude the icon we just dropped)
                        const iconsToShift = window.icons.filter(icon => 
                            icon.kfolder === folderId && icon.id !== savedIconId
                        );
                        
                        // Shift all icons
                        iconsToShift.forEach(icon => {
                            icon.region += regionsPerScreen;
                        });
                        
                        // Batch update all shifted icons
                        await api.updateIcons(iconsToShift);
                        
                        // Verify result
                        if (result.success) {
                            const expectedIcons = window.icons.filter(ic => ic.kfolder === folderId).length;
                            if (result.total_icons !== expectedIcons) {
                                console.warn(`Icon count mismatch after insertRegions: expected ${expectedIcons}, got ${result.total_icons}`);
                            }
                        }
                    } else if (whichSide === 'right') {
                        const tempScreens = screensContainer.querySelectorAll('.folder-screen.temporary');
                        tempScreens.forEach(screen => {
                            screen.classList.remove('temporary');
                        });
                    }
                    
                    state.temporaryScreenSide = null;
                    
                    const folderIcons = window.icons.filter(ic => ic.kfolder === folderId);
                    const screens = render.organizeIntoScreens(folderIcons, true);
                    
                    render.renderPageIndicators(screens.length, 'folderPageIndicators');
                    
                    const currentScreenDiv = screensContainer.children[state.currentFolderScreen];
                    if (currentScreenDiv) {
                        const startRegion = state.currentFolderScreen * regionsPerScreen;
                        const screenRegions = [];
                        
                        for (let i = 0; i < regionsPerScreen; i++) {
                            const regionNum = startRegion + i;
                            const regionIcons = folderIcons.filter(ic => ic.region === regionNum);
                            screenRegions.push({ regionNum, icons: regionIcons });
                        }
                        
                        currentScreenDiv.innerHTML = render.renderScreen(screenRegions, true);
                    }

                    let originalScreenIndex = Math.floor(savedOriginalRegion / regionsPerScreen);
                    if (whichSide === 'left') {
                        originalScreenIndex += 1;
                    }

                    if (originalScreenIndex !== state.currentFolderScreen && screensContainer.children[originalScreenIndex]) {
                        const originalScreenDiv = screensContainer.children[originalScreenIndex];
                        const originalStartRegion = originalScreenIndex * regionsPerScreen;
                        const originalScreenRegions = [];
                        
                        for (let i = 0; i < regionsPerScreen; i++) {
                            const regionNum = originalStartRegion + i;
                            const regionIcons = folderIcons.filter(ic => ic.region === regionNum);
                            originalScreenRegions.push({ regionNum, icons: regionIcons });
                        }
                        
                        originalScreenDiv.innerHTML = render.renderScreen(originalScreenRegions, true);
                    }

                    render.updateFolderHeight();
                    folder.attachFolderEventListeners();
                    
                    // Check for empty screens
                    await nav.cleanupEmptyFolderScreens();
                    
                    // Re-select the icon after insert/append in keyboard move mode
                    if (needsReselect) {
                        await reselectIconAfterInsert(savedIconId);
                    }
                } else {
                    // ===== MAIN SCREEN TEMPORARY SCREEN =====
                    const regionsPerScreen = layout.leaves * layout.bands;
                    
                    if (whichSide === 'left') {
                        const screensContainer = document.getElementById('appspaceScreens');
                        const tempScreen = screensContainer.querySelector('.appspace-screen.temporary');
                        if (tempScreen) {
                            tempScreen.classList.remove('temporary');
                        }
                        
                        // Insert regions on backend
                        const result = await api.insertRegions(0, regionsPerScreen, 0);
                        
                        // Collect icons to shift
                        const iconsToShift = window.icons.filter(icon => 
                            icon.kfolder === 0 && icon.id !== savedIconId
                        );
                        
                        // Shift all icons
                        iconsToShift.forEach(icon => {
                            icon.region += regionsPerScreen;
                        });
                        
                        // Batch update all shifted icons
                        await api.updateIcons(iconsToShift);
                        
                        // Verify result
                        if (result.success) {
                            const expectedIcons = window.icons.filter(ic => ic.kfolder === 0).length;
                            if (result.total_icons !== expectedIcons) {
                                console.warn(`Icon count mismatch after insertRegions: expected ${expectedIcons}, got ${result.total_icons}`);
                            }
                        }
                    } else if (whichSide === 'right') {
                        const screensContainer = document.getElementById('appspaceScreens');
                        const tempScreens = screensContainer.querySelectorAll('.appspace-screen.temporary');
                        tempScreens.forEach(screen => screen.classList.remove('temporary'));
                    }
                    
                    state.temporaryScreenSide = null;
                    
                    if (savedOriginalFolder !== 0) {
                        const wasDeleted = await api.checkAndDeleteEmptyFolder(savedOriginalFolder);
                        if (wasDeleted && state.openFolder && state.openFolder.id === savedOriginalFolder) {
                            folder.closeFolderView();
                            render.renderLaunchpad();
                            return;
                        }
                    }
                    
                    await nav.cleanupEmptyScreens();
                    render.renderLaunchpad();
                    
                    // Re-select the icon after insert/append in keyboard move mode
                    if (needsReselect) {
                        await reselectIconAfterInsert(savedIconId);
                    }
                }
            })();
        } else {
            // Not on temporary screen - just normal cleanup
            if (state.temporaryScreenSide) {
                if (state.openFolder) {
                    nav.removeTemporaryFolderScreens();
                } else {
                    nav.removeTemporaryScreens();
                }
            }

            if (state.openFolder) {
                const folderIcons = window.icons.filter(ic => ic.kfolder === state.openFolder.id);
                const screens = render.organizeIntoScreens(folderIcons, true);
                const layout = render.calculateLayout();
                const regionsPerScreen = layout.leaves;
                
                render.renderPageIndicators(screens.length, 'folderPageIndicators');
                
                const screensContainer = document.getElementById('folderScreens');
                const currentScreenDiv = screensContainer.children[state.currentFolderScreen];
                
                if (currentScreenDiv) {
                    const startRegion = state.currentFolderScreen * regionsPerScreen;
                    const screenRegions = [];
                    
                    for (let i = 0; i < regionsPerScreen; i++) {
                        const regionNum = startRegion + i;
                        const regionIcons = folderIcons.filter(ic => ic.region === regionNum);
                        screenRegions.push({ regionNum, icons: regionIcons });
                    }
                    
                    currentScreenDiv.innerHTML = render.renderScreen(screenRegions, true);
                }

                let originalScreenIndex = Math.floor(savedOriginalRegion / regionsPerScreen);
                // Don't adjust for temporary screen here - it was removed if it existed

                if (originalScreenIndex !== state.currentFolderScreen && screensContainer.children[originalScreenIndex]) {
                    const originalScreenDiv = screensContainer.children[originalScreenIndex];
                    const originalStartRegion = originalScreenIndex * regionsPerScreen;
                    const originalScreenRegions = [];
                    
                    for (let i = 0; i < regionsPerScreen; i++) {
                        const regionNum = originalStartRegion + i;
                        const regionIcons = folderIcons.filter(ic => ic.region === regionNum);
                        originalScreenRegions.push({ regionNum, icons: regionIcons });
                    }
                    
                    originalScreenDiv.innerHTML = render.renderScreen(originalScreenRegions, true);
                }

                render.updateFolderHeight();                
                folder.attachFolderEventListeners();
                
                // Check for empty screens
                (async () => {
                    await nav.cleanupEmptyFolderScreens();
                    
                    // Re-select the icon after insert/append in keyboard move mode
                    if (needsReselect) {
                        await reselectIconAfterInsert(savedIconId);
                    }
                })();
            } else {
                render.renderLaunchpad();
                
                // Check for empty screens
                (async () => {
                    await nav.cleanupEmptyScreens();
                    
                    // Re-select the icon after insert/append in keyboard move mode
                    if (needsReselect) {
                        await reselectIconAfterInsert(savedIconId);
                    }
                })();
            }
        }
    }
}

function animateIconBack() {
    if (!state.dragGhost) {
        cancelDragging();
        return;
    }

    // Restore icon to original state
    if (state.dragState) {
        state.dragState.icon.kfolder = state.dragState.originalFolder;
        state.dragState.icon.region = state.dragState.originalRegion;
        state.dragState.icon.position = state.dragState.originalPosition;
        api.updateIcon(state.dragState.icon);
    }

    // Save whether we're on a temporary screen
    const wasOnTemporaryScreen = state.temporaryScreenSide !== null;
    
    // If icon came from a folder that's now closed, animate to folder location
    if (state.dragState.originalFolder !== 0 && !state.openFolder) {
        // Find the folder icon on the main screen
        const folderIcon = window.icons.find(ic => ic.id === state.dragState.originalFolder);
        if (folderIcon) {
            const folderSlot = document.querySelector(
                `.icon-slot[data-region="${folderIcon.region}"][data-position="${folderIcon.position}"]`
            );
            
            if (folderSlot) {
                const targetRect = folderSlot.getBoundingClientRect();
                const currentRect = state.dragGhost.getBoundingClientRect();
                

                // Animate to folder location with shrink effect
                const ghostWidth = currentRect.width;
                const ghostHeight = currentRect.height;
                
                state.dragGhost.style.transition = 'all 0.4s cubic-bezier(0.4, 0.0, 0.2, 1)';
                state.dragGhost.style.left = (targetRect.left + targetRect.width / 2 - ghostWidth / 2) + 'px';
                state.dragGhost.style.top = (targetRect.top + targetRect.height / 2 - ghostHeight / 2) + 'px';
                state.dragGhost.style.transform = 'scale(0.25)';
                state.dragGhost.style.opacity = '0.5';
                
                setTimeout(() => {
                    cancelDragging();
                    
                    if (wasOnTemporaryScreen) {
                        nav.removeTemporaryScreens();
                        nav.goToScreen(0);
                        
                        const rootIcons = window.icons.filter(icon => icon.kfolder === 0);
                        const screens = render.organizeIntoScreens(rootIcons, false);
                        render.renderPageIndicators(screens.length, 'pageIndicators');
                    }
                    
                    render.renderLaunchpad();
                }, 400);
                return;
            }
        }
        
        // Fallback if folder not found
        cancelDragging();
        
        if (wasOnTemporaryScreen) {
            nav.removeTemporaryScreens();
            nav.goToScreen(0);
            
            const rootIcons = window.icons.filter(icon => icon.kfolder === 0);
            const screens = render.organizeIntoScreens(rootIcons, false);
            render.renderPageIndicators(screens.length, 'pageIndicators');
        }
        
        render.renderLaunchpad();
        return;
    }

    // Find the original icon slot
    const originalSlot = document.querySelector(
        `.icon-slot[data-region="${state.dragState.originalRegion}"][data-position="${state.dragState.originalPosition}"]`
    );
    
    if (originalSlot) {
        const targetRect = originalSlot.getBoundingClientRect();
        
        // Animate
        state.dragGhost.style.transition = 'all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)';
        state.dragGhost.style.left = targetRect.left + 'px';
        state.dragGhost.style.top = targetRect.top + 'px';
        state.dragGhost.style.opacity = '0';
        
        setTimeout(() => {
            cancelDragging();
            
            // Clean up temporary screens if we were on one
            if (wasOnTemporaryScreen) {
                if (state.openFolder) {
                    nav.removeTemporaryFolderScreens();
                    
                    // Scroll back to a valid screen (screen 0 or original screen)
                    const targetScreen = Math.min(state.currentFolderScreen, 
                        document.getElementById('folderScreens').children.length - 1);
                    nav.goToFolderScreen(targetScreen > 0 ? targetScreen - 1 : 0);
                    
                    // Update indicators
                    const folderIcons = window.icons.filter(ic => ic.kfolder === state.openFolder.id);
                    const screens = render.organizeIntoScreens(folderIcons, true);
                    render.renderPageIndicators(screens.length, 'folderPageIndicators');
                } else {
                    nav.removeTemporaryScreens();
                    
                    // Scroll back to a valid screen
                    const screensContainer = document.getElementById('appspaceScreens');
                    const targetScreen = Math.min(state.currentScreen, 
                        screensContainer.children.length - 1);
                    nav.goToScreen(targetScreen > 0 ? targetScreen - 1 : 0);
                    
                    // Update indicators
                    const rootIcons = window.icons.filter(icon => icon.kfolder === 0);
                    const screens = render.organizeIntoScreens(rootIcons, false);
                    render.renderPageIndicators(screens.length, 'pageIndicators');
                }
            }
            
            // Regular cleanup - don't call folder.openFolderView to avoid scroll animation
            if (state.dragState && state.dragState.originalFolder !== 0 && !state.openFolder) {
                render.renderLaunchpad();
            } else if (state.openFolder) {
                // Don't re-render folder - already cleaned up temporary screens above
            } else {
                // Already cleaned up temporary screens above if needed
            }
        }, 300);
    } else {
        // Can't find original slot, just cancel and clean up
        cancelDragging();
        
        if (wasOnTemporaryScreen) {
            if (state.openFolder) {
                nav.removeTemporaryFolderScreens();
                nav.goToFolderScreen(0);
                
                const folderIcons = window.icons.filter(ic => ic.kfolder === state.openFolder.id);
                const screens = render.organizeIntoScreens(folderIcons, true);
                render.renderPageIndicators(screens.length, 'folderPageIndicators');
            } else {
                nav.removeTemporaryScreens();
                nav.goToScreen(0);
                
                const rootIcons = window.icons.filter(icon => icon.kfolder === 0);
                const screens = render.organizeIntoScreens(rootIcons, false);
                render.renderPageIndicators(screens.length, 'pageIndicators');
            }
        }
    }
}

export function cancelDragging() {
    if (state.dragGhost) {
        state.dragGhost.remove();
        state.dragGhost = null;
    }
    
    // Clean up all interaction flags
    state.isDraggingScreen = false;
    state.isInteractingWithIcon = false;
    state.iconPressStart = null;
    
    const screensContainer = document.getElementById('appspaceScreens');
    const folderScreensContainer = document.getElementById('folderScreens');
    if (screensContainer) {
        screensContainer.classList.remove('dragging');
        nav.goToScreen(state.currentScreen);
    }
    if (folderScreensContainer) {
        folderScreensContainer.classList.remove('dragging');
        nav.goToFolderScreen(state.currentFolderScreen);
    }
    
    document.querySelectorAll('.drag-placeholder').forEach(el => {
        el.classList.remove('drag-placeholder');
    });
       
    document.querySelectorAll('.appicon.drag-over').forEach(el => {
        el.classList.remove('drag-over');
    });
    
    // Clear ALL indicators
    [document.getElementById('insertIndicator'), 
     document.getElementById('folderInsertIndicator')].forEach(ind => {
        if (ind) {
            ind.classList.remove('visible');
            ind.style.left = '';
            ind.style.top = '';
            ind.style.width = '';
            ind.style.height = '';
        }
    });
    
    [document.getElementById('dropPreview'),
     document.getElementById('folderDropPreview')].forEach(preview => {
        if (preview) {
            preview.classList.remove('visible');
            preview.style.left = '';
            preview.style.top = '';
            preview.style.width = '';
            preview.style.height = '';
        }
    });
    
    document.getElementById('scrollZoneLeft')?.classList.remove('active');
    document.getElementById('scrollZoneRight')?.classList.remove('active');
    document.getElementById('folderScrollZoneLeft')?.classList.remove('active');
    document.getElementById('folderScrollZoneRight')?.classList.remove('active');
    
    clearTimeout(state.scrollHoverTimer);
    state.scrollHoverTimer = null;

    clearTimeout(state.folderCloseTimer);
    state.folderCloseTimer = null;
    
    // Remove temporary screens
    if (state.temporaryScreenSide) {
        nav.removeTemporaryScreens();
    }
    
    state.dragState = null;
    state.setDraggingState(false);
}
